import React, { useState } from 'react';
import { Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectConfigSelections, selectConfigFilling,
  selectConfigPerCell,
  selectConfigWeekPortions,
  selectConfigTargets,
  selectConfigPlan,
  selectConfigShown,
  selectConfigWindow,
  toggleSlot,
  setSelections,
  setCellOverride,
  removeCellOverride,
  setWeekPortions,
  setConfigTargets,
} from '../../../redux/mealPlanUISlice';
import { selectRecipeIndex } from '../../../redux/recipeIndexSlice';
import {
  MEALS,
  DAY_FULL_KEYS,
  dayFullKey,
  DAY_PROFILE,
  slotKey,
  dayOf,
} from '../../weekly/v2/constants';
import { useTranslation } from 'react-i18next';
import { resolveRecipeTitle } from '../../../utils/recipeTitle';
import { Paprika } from '../../../styles/theme';
import { addDays, dayIndexInWeek, weekStartOf } from '../../../utils/localDate';
import { ConfigRow } from './ConfigRow';
import { SuggCardM } from './SuggCardM';
import { CellSheet } from './CellSheet';
import { WeekSheet } from './WeekSheet';
import { WeekSettingsCard } from './WeekSettingsCard';
import { ShimmerBox } from '../../weekly/v2/primitives/ShimmerBox';
import type { ConfigPhase, DietKind } from '../../../redux/mealPlanUISlice';
import type { DietaryRole } from '../../../redux/mealPlanSlice';

interface Props {
  phase: ConfigPhase;
  onCellShuffle: (sk: number) => void;
  onPortionsTap: (sk: number) => void;
}

function kindToDietaryRole(kind: DietKind): DietaryRole | undefined {
  if (kind === 'fish') return 'fish';
  if (kind === 'veg') return 'vegetarian';
  return undefined;
}

// Static slot sets for the quick-select chips (mirror desktop ConfigGrid).
const DINNER_SLOTS = Array.from({ length: 7 }, (_, di) => slotKey(2, di)); // soupers
const LUNCH_SLOTS = Array.from({ length: 7 }, (_, di) => slotKey(1, di)); // dîners
const ALL_SLOTS = (() => {
  const slots: number[] = [];
  for (let mi = 0; mi < 3; mi++) {
    for (let di = 0; di < 7; di++) {
      if (MEALS[mi].weekendOnly && di < 5) continue;
      slots.push(slotKey(mi, di));
    }
  }
  return slots;
})();

function QuickChip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.chip, active && styles.chipActive]}>
      {active && <Feather name="check" size={11} color={Paprika.primary} />}
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
    </Pressable>
  );
}

export function ConfigDayList({ phase, onCellShuffle, onPortionsTap }: Props) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const selections = useAppSelector(selectConfigSelections);
  // A generation started from the week view has no config selection: the
  // slots it fills are what the thinking shimmer and the cascade run on.
  const filling = useAppSelector(selectConfigFilling);
  const perCell = useAppSelector(selectConfigPerCell);
  const defaultPortions = useAppSelector(selectConfigWeekPortions);
  const targets = useAppSelector(selectConfigTargets);
  const configPlan = useAppSelector(selectConfigPlan);
  const configShown = useAppSelector(selectConfigShown);
  const recipeIndex = useAppSelector(selectRecipeIndex);
  // The running generation's first day: today on the phone (#808), so column 0
  // may be a Sunday. Outside a generation the list is the Monday week.
  const windowStart = useAppSelector(selectConfigWindow);
  const weekdayOf = (di: number): number => {
    if (!windowStart) return di;
    const date = addDays(windowStart, di);
    return dayIndexInWeek(weekStartOf(date), date) ?? di;
  };

  const [cellSheetFor, setCellSheetFor] = useState<number | null>(null);
  const [weekSheetVisible, setWeekSheetVisible] = useState(false);

  const isConfig = phase === 'config';
  const isThinking = phase === 'thinking';
  const isCascadeOrDone = phase === 'cascade' || phase === 'done';

  function handleRowPress(sk: number, selected: boolean) {
    if (isCascadeOrDone) return;
    if (selected) {
      setCellSheetFor(sk);
    } else {
      dispatch(toggleSlot(sk));
    }
  }

  const activeCellSk = cellSheetFor;
  const activeCellOverride = activeCellSk !== null ? perCell[activeCellSk] : null;
  const activeCellPortions = activeCellOverride?.portions ?? defaultPortions;
  const activeCellKind = activeCellOverride?.kind ?? 'auto';

  // Quick-select chip active states + toggle helper (mirror desktop ConfigGrid).
  const dinnersActive = DINNER_SLOTS.every((s) => selections.includes(s));
  const lunchesActive = LUNCH_SLOTS.every((s) => selections.includes(s));
  const allActive = ALL_SLOTS.every((s) => selections.includes(s));
  function toggleSet(set: number[], active: boolean) {
    if (active) {
      dispatch(setSelections(selections.filter((s) => !set.includes(s))));
    } else {
      dispatch(setSelections([...new Set([...selections, ...set])]));
    }
  }

  return (
    <>
      <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false} contentContainerStyle={styles.container}>
        {/* Week settings summary card + quick-select chips */}
        {isConfig && (
          <View style={styles.settingsHeader}>
            <WeekSettingsCard
              portions={defaultPortions}
              fish={targets.fish}
              veg={targets.veg}
              meat={targets.meat}
              onPress={() => setWeekSheetVisible(true)}
            />
            <View style={styles.chipRow}>
              <QuickChip label={t('mealConfig.wholeWeek')} active={allActive} onPress={() => toggleSet(ALL_SLOTS, allActive)} />
              <QuickChip label={t('mealConfig.dinners')} active={dinnersActive} onPress={() => toggleSet(DINNER_SLOTS, dinnersActive)} />
              <QuickChip label={t('mealConfig.lunches')} active={lunchesActive} onPress={() => toggleSet(LUNCH_SLOTS, lunchesActive)} />
              {selections.length > 0 && (
                <Pressable onPress={() => dispatch(setSelections([]))} style={styles.clearChip}>
                  <Text style={styles.clearChipText}>{t('mealConfig.clear')}</Text>
                </Pressable>
              )}
            </View>
          </View>
        )}

        {/* Day blocks */}
        {DAY_FULL_KEYS.map((_dayKey, di) => {
          const wd = weekdayOf(di);
          const dayLabel = t(dayFullKey(wd));
          const profile = DAY_PROFILE[wd];
          const applicableMeals = MEALS.filter((m, mi) => {
            if (m.weekendOnly && wd < 5) return false;
            return true;
          });

          if (applicableMeals.length === 0) return null;
          // A day the run does not write is not drawn: under tonight's date, an
          // empty « Souper » read as the dinner gone (#862).
          if (windowStart && !isConfig && !filling.some((sk) => dayOf(sk) === di)) return null;

          return (
            <View key={di} style={styles.dayBlock}>
              {/* Day header */}
              <View style={styles.dayHeader}>
                <Text style={styles.dayName} testID={`config-day-${di}`}>{dayLabel}</Text>
                <View style={[styles.dayTagBadge, { backgroundColor: profile.accent + '22' }]}>
                  <Text style={[styles.dayTagText, { color: profile.accent }]}>{t(profile.tagKey)}</Text>
                </View>
              </View>

              {/* Meal rows — weekday Déjeuner stays hidden unless already selected (spec) */}
              {MEALS.map((meal, mi) => {
                const sk = slotKey(mi, di);
                // While generating, the cells are the ones being filled: a
                // tick left from the config screen belongs to its own week.
                const selected = isConfig ? selections.includes(sk) : filling.includes(sk);
                if (meal.weekendOnly && wd < 5 && !selected) return null;
                const override = perCell[sk];
                const portions = override?.portions ?? defaultPortions;
                const kind = override?.kind ?? 'auto';
                const recipeId = configPlan[sk];
                const recipe = recipeId ? recipeIndex.find((r) => r.id === recipeId) : undefined;
                const shown = configShown.includes(sk);

                if (isCascadeOrDone && selected) {
                  if (recipe) {
                    return (
                      <SuggCardM
                        key={mi}
                        title={resolveRecipeTitle(recipe, i18n.language)}
                        image={recipe.image}
                        portions={portions}
                        minutes={recipe.readyInMinutes}
                        dietaryRole={kindToDietaryRole(kind)}
                        shown={shown}
                        onShuffle={() => onCellShuffle(sk)}
                        onPortionsTap={() => onPortionsTap(sk)}
                      />
                    );
                  }
                  return (
                    <View key={mi} style={styles.shimmerWrap}>
                      <ShimmerBox width="100%" height={200} borderRadius={10} />
                    </View>
                  );
                }

                if (isThinking && selected) {
                  return (
                    <View key={mi} style={styles.shimmerWrap}>
                      <ShimmerBox width="100%" height={80} borderRadius={10} />
                    </View>
                  );
                }

                return (
                  <ConfigRow
                    key={mi}
                    label={t(meal.labelKey)}
                    selected={selected}
                    portions={portions}
                    kind={kind}
                    onPress={() => handleRowPress(sk, selected)}
                  />
                );
              })}
            </View>
          );
        })}
      </ScrollView>

      {/* Cell sheet */}
      {activeCellSk !== null && (
        <CellSheet
          visible
          mealLabel={MEALS[Math.floor(activeCellSk / 7)] ? t(MEALS[Math.floor(activeCellSk / 7)].labelKey) : ''}
          portions={activeCellPortions}
          kind={activeCellKind}
          defaultPortions={defaultPortions}
          hasPortionsOverride={activeCellOverride?.portions != null && activeCellOverride.portions !== defaultPortions}
          onResetPortions={() => {
            if (activeCellSk !== null) {
              dispatch(setCellOverride({ slotKey: activeCellSk, override: { portions: defaultPortions, kind: activeCellKind } }));
            }
          }}
          onPortionsChange={(n) => {
            if (activeCellSk !== null) {
              dispatch(setCellOverride({ slotKey: activeCellSk, override: { portions: n, kind: activeCellKind } }));
            }
          }}
          onKindChange={(k) => {
            if (activeCellSk !== null) {
              dispatch(setCellOverride({ slotKey: activeCellSk, override: { portions: activeCellPortions, kind: k } }));
            }
          }}
          onRemove={() => {
            if (activeCellSk !== null) {
              dispatch(removeCellOverride(activeCellSk));
              dispatch(toggleSlot(activeCellSk));
            }
            setCellSheetFor(null);
          }}
          onConfirm={() => setCellSheetFor(null)}
          onClose={() => setCellSheetFor(null)}
        />
      )}

      {/* Week sheet */}
      <WeekSheet
        visible={weekSheetVisible}
        portions={defaultPortions}
        fish={targets.fish}
        veg={targets.veg}
        meat={targets.meat}
        onPortionsChange={(n) => dispatch(setWeekPortions(n))}
        onFishChange={(n) => dispatch(setConfigTargets({ ...targets, fish: n }))}
        onVegChange={(n) => dispatch(setConfigTargets({ ...targets, veg: n }))}
        onMeatChange={(n) => dispatch(setConfigTargets({ ...targets, meat: n }))}
        onClose={() => setWeekSheetVisible(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  container: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 24,
  },
  settingsHeader: {
    gap: 10,
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.surfaceLowest,
  },
  chipActive: {
    backgroundColor: Paprika.brandTint,
    borderColor: Paprika.brandSoft,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  chipTextActive: {
    color: Paprika.primary,
    fontFamily: 'Inter-SemiBold',
  },
  clearChip: {
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  clearChipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  dayBlock: {
    gap: 0,
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  dayName: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 18,
    color: Paprika.textPrimary,
  },
  dayTagBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  dayTagText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.3,
  },
  shimmerWrap: {
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 8,
  },
});
